import { EditorPlugin } from "@draft-js-plugins/editor";
import createLinkifyPlugin from "@draft-js-plugins/linkify";
import createMentionPlugin, { MentionData } from "@draft-js-plugins/mention";
import { SubMentionComponentProps } from "@draft-js-plugins/mention/lib/Mention";
import { EntryComponentProps } from "@draft-js-plugins/mention/lib/MentionSuggestions/Entry/Entry";
import { MentionSuggestionsPubProps } from "@draft-js-plugins/mention/lib/MentionSuggestions/MentionSuggestions";
import { ContentState, convertFromRaw, convertToRaw, RawDraftEntityRange } from "draft-js";
import debounce from "lodash/debounce";
import React, { ComponentType, useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { ImageVersions } from "@domain/media/dtos";
import { PersonType } from "@domain/user/user";
import { fullName } from "@domain/user/utils";
import { gap } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { UserView } from "@ui/components/profile/userView";
import { createAddLinkPlugin } from "./addLinkPlugin";

const SUGGESTIONS_MAX_SIZE = 240;
interface MentionPluginInfos {
	MentionSuggestions?: ComponentType<MentionSuggestionsPubProps>;
	EntryComponent?: ComponentType<EntryComponentProps>;
	plugins: EditorPlugin[];
	open: boolean;
	setOpen: (open: boolean) => void;
	suggestions: MentionData[];
	onSearchChange: (v: { value: string }) => void;
}
export function useDraftPlugins(mentionsEnabled?: boolean): MentionPluginInfos {
	const { userService } = useServices();
	const [open, setOpen] = useState(true);
	const [suggestions, setSuggestions] = useState<MentionData[]>([]);

	const { MentionSuggestions, mentionPlugins } = useMemo(() => {
		if (!mentionsEnabled) {
			return { mentionPlugins: [] };
		}

		const mentionPlugin = createMentionPlugin({
			mentionComponent: MentionComponent,
			positionSuggestions: ({ decoratorRect }) => {
				const bottomOffset = decoratorRect.bottom - window.innerHeight;
				return { transform: bottomOffset > -SUGGESTIONS_MAX_SIZE ? "translateY(calc(-100% - 40px))" : "initial" };
			},
			supportWhitespace: true,
		});
		const { MentionSuggestions } = mentionPlugin;
		return { mentionPlugins: [mentionPlugin], MentionSuggestions };
	}, [mentionsEnabled]);

	const plugins = useMemo(() => {
		const linkifyPlugin = createLinkifyPlugin({ target: "_blank" });
		const addLinkPlugin = createAddLinkPlugin({ editable: true });
		const plugins = [...mentionPlugins, linkifyPlugin, addLinkPlugin];
		return plugins;
	}, [mentionPlugins]);

	const onSearchChange = useCallback(
		debounce(
			async ({ value }: { value: string }) => {
				const persons = await userService.getMentions(value);
				setSuggestions(
					persons.map((person) => ({
						name: fullName(person),
						avatar: person.avatar?.versions?.[ImageVersions.SMALL] ?? person.avatar?.url,
						id: person.id,
						slug: person.slug,
						type: person.type,
					}))
				);
			},
			500,
			{ leading: true }
		),
		[]
	);

	return {
		MentionSuggestions,
		EntryComponent,
		plugins,
		open: open && suggestions.length > 0,
		setOpen,
		suggestions,
		onSearchChange,
	};
}

const getIndicesOf = (searchStr: string, str: string, caseSensitive?: boolean): number[] => {
	let tempStr = str;
	let tempSearchStr = searchStr;
	const searchStrLen = tempSearchStr.length;
	if (searchStrLen === 0) {
		return [];
	}
	let startIndex = 0;
	let index;
	const indices = [];
	if (!caseSensitive) {
		tempStr = tempStr.toLowerCase();
		tempSearchStr = tempSearchStr.toLowerCase();
	}

	while ((index = tempStr.indexOf(tempSearchStr, startIndex)) > -1) {
		indices.push(index);
		startIndex = index + searchStrLen;
	}
	return indices;
};

const getEntityRanges = (text: string, mentionName: string, mentionKey: number): Array<RawDraftEntityRange> | null => {
	const indices = getIndicesOf(mentionName, text);
	if (indices.length > 0) {
		const ranges = indices.map((offset) => ({
			key: mentionKey,
			length: mentionName.length,
			offset,
		}));
		return ranges;
	}
	return null;
};

export const createMentionEntities = (
	text: string,
	tags: { mention: { id: string; name: string; type: PersonType; slug?: string } }[]
): ContentState => {
	const rawContent = convertToRaw(ContentState.createFromText(text));
	const rawState = tags.map((tag) => ({
		type: "mention",
		mutability: "IMMUTABLE",
		data: { mention: { id: tag.mention.id, name: tag.mention.name, slug: tag.mention.slug, type: tag.mention.type } },
	}));
	const entityMap = [...rawState];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	rawContent.entityMap = entityMap as any;

	rawContent.blocks = rawContent.blocks.map((block) => {
		const ranges: Array<RawDraftEntityRange> = [];
		tags.forEach((tag, index) => {
			const entityRanges = getEntityRanges(block.text, tag.mention.name, index);
			if (entityRanges) {
				ranges.push(...entityRanges);
			}
		});
		return { ...block, entityRanges: ranges };
	});
	return convertFromRaw(rawContent);
};

export const MentionComponent: React.FC<SubMentionComponentProps> = ({ mention, children, className }) => {
	if (!mention) {
		return null;
	}
	return (
		<Link
			to={
				["INSTITUTE", "COMPANY"].includes(mention.type as PersonType)
					? Path.Organization(mention as { id?: string; slug?: string })
					: Path.Profile(mention as { id?: string; slug?: string })
			}
		>
			<MentionRenderer className={className}>{children}</MentionRenderer>
		</Link>
	);
};

const EntryComponent: React.FC<EntryComponentProps> = ({ mention, theme, searchValue, isFocused, ...parentProps }) => {
	return (
		<div {...parentProps}>
			<UserView
				disabled
				user={{
					type: "USER",
					primaryName: mention.name,
					avatar: { id: mention.id as string, url: mention.avatar ?? "", mimeType: "image/png", displayName: "" },
					id: mention.id as string,
					description: "",
				}}
			/>
		</div>
	);
};

const MentionRenderer = styled.span`
	color: ${({ theme }) => theme.colors.primary};
	background-color: transparent;
	&:hover {
		color: ${({ theme }) => theme.colors.primary};
		background-color: transparent;
	}
`;

export const PopOverComponent = styled(RoundedBlock)`
	padding: 10px;
	z-index: ${Z.above(Z.toolbar)};
	${gap(10)}
`;
